import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Building2, Users, DollarSign, Copy, ExternalLink, LogOut,
  TrendingUp, Ticket, Calendar, CreditCard, Check, Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [agency, setAgency] = useState(null);
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [processingPayout, setProcessingPayout] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('agencyToken');

  useEffect(() => {
    if (!token) {
      navigate('/agency/login');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [profileRes, statsRes, creatorsRes, commissionsRes, payoutsRes, linkRes] = await Promise.all([
        axios.get(`${API_URL}/api/agency/me`, { headers }),
        axios.get(`${API_URL}/api/agency/stats`, { headers }),
        axios.get(`${API_URL}/api/agency/creators`, { headers }),
        axios.get(`${API_URL}/api/agency/commissions`, { headers }),
        axios.get(`${API_URL}/api/agency/payouts`, { headers }),
        axios.get(`${API_URL}/api/agency/referral-link`, { headers })
      ]);

      setAgency(profileRes.data);
      setStats(statsRes.data);
      setCreators(creatorsRes.data.creators || []);
      setCommissions(commissionsRes.data.commissions || []);
      setPayouts(payoutsRes.data.payouts || []);
      setReferralLink(linkRes.data.referral_link);
      setPaypalEmail(profileRes.data.paypal_email || '');
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('agencyToken');
        localStorage.removeItem('agencyData');
        navigate('/agency/login');
      } else {
        toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({ title: 'Copied!', description: 'Referral link copied to clipboard' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUpdatePaypal = async () => {
    try {
      await axios.put(
        `${API_URL}/api/agency/paypal-email`,
        { paypal_email: paypalEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Success', description: 'PayPal email updated' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update', variant: 'destructive' });
    }
  };

  const handlePayout = async () => {
    if (!paypalEmail) {
      toast({ title: 'Error', description: 'Please set your PayPal email first', variant: 'destructive' });
      return;
    }
    
    const amount = parseFloat(payoutAmount);
    if (!amount || amount < 10) {
      toast({ title: 'Error', description: 'Minimum payout is $10', variant: 'destructive' });
      return;
    }

    setProcessingPayout(true);
    try {
      await axios.post(
        `${API_URL}/api/agency/payout`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Success', description: `Payout of $${amount.toFixed(2)} initiated!` });
      setPayoutAmount('');
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Payout failed', variant: 'destructive' });
    } finally {
      setProcessingPayout(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('agencyToken');
    localStorage.removeItem('agencyData');
    navigate('/agency/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{agency?.agency_name}</h1>
              <p className="text-xs text-gray-400">Agency Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white text-sm">
              FanBound Home
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Referral Link Banner */}
        <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-700/50 rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-2">Your Referral Link</h2>
          <p className="text-gray-400 text-sm mb-4">Share this link with creators to earn 15% of platform commission</p>
          <div className="flex gap-2">
            <Input
              value={referralLink}
              readOnly
              className="bg-gray-900/50 border-gray-700 text-white font-mono text-sm"
            />
            <Button onClick={handleCopyLink} className="bg-purple-600 hover:bg-purple-700 shrink-0">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-gray-400 text-sm">Creators</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_creators || 0}</p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-gray-400 text-sm">Total Earnings</span>
            </div>
            <p className="text-2xl font-bold text-white">${(stats?.total_earnings || 0).toFixed(2)}</p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-yellow-400" />
              </div>
              <span className="text-gray-400 text-sm">Pending Balance</span>
            </div>
            <p className="text-2xl font-bold text-white">${(stats?.pending_balance || 0).toFixed(2)}</p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Ticket className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-gray-400 text-sm">Tickets Sold</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_tickets_sold || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['overview', 'creators', 'commissions', 'payouts', 'settings'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'bg-purple-600' : 'text-gray-400'}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-2xl p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white">Overview</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-900/50 rounded-xl p-4">
                  <h4 className="text-gray-400 text-sm mb-4">Commission Structure</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• FanBound takes 30% platform fee on ticket sales</li>
                    <li>• You earn 15% of our platform fee</li>
                    <li>• That's 4.5% of every ticket sold by your creators</li>
                    <li>• Minimum payout: $10</li>
                  </ul>
                </div>
                <div className="bg-gray-900/50 rounded-xl p-4">
                  <h4 className="text-gray-400 text-sm mb-4">Quick Stats</h4>
                  <ul className="space-y-2">
                    <li className="flex justify-between text-gray-300">
                      <span>Total Events</span>
                      <span className="font-semibold text-white">{stats?.total_events || 0}</span>
                    </li>
                    <li className="flex justify-between text-gray-300">
                      <span>Total Paid Out</span>
                      <span className="font-semibold text-green-400">${(stats?.paid_out || 0).toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between text-gray-300">
                      <span>Commission Transactions</span>
                      <span className="font-semibold text-white">{stats?.commission_count || 0}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'creators' && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Your Creators ({creators.length})</h3>
              {creators.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No creators yet. Share your referral link!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {creators.map((creator) => (
                    <div key={creator.id} className="bg-gray-900/50 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {creator.avatar_url ? (
                          <img src={creator.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold">{creator.display_name?.[0]}</span>
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{creator.display_name}</p>
                          <p className="text-gray-400 text-sm">{creator.username}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-semibold">${(creator.agency_earnings || 0).toFixed(2)}</p>
                        <p className="text-gray-400 text-xs">{creator.event_count || 0} events</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'commissions' && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Commission History</h3>
              {commissions.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No commissions yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {commissions.map((c) => (
                    <div key={c.id} className="bg-gray-900/50 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{c.event_title}</p>
                        <p className="text-gray-400 text-xs">{c.creator_name} • {new Date(c.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-semibold">+${c.agency_share.toFixed(2)}</p>
                        <p className="text-gray-500 text-xs">Platform: ${c.platform_commission.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'payouts' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Request Payout</h3>
                <p className="text-gray-400">Available: <span className="text-green-400 font-semibold">${(agency?.pending_balance || 0).toFixed(2)}</span></p>
              </div>
              
              {!agency?.paypal_email ? (
                <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-xl p-4">
                  <p className="text-yellow-300">Please set your PayPal email in Settings first</p>
                </div>
              ) : (
                <div className="flex gap-4">
                  <Input
                    type="number"
                    placeholder="Amount (min $10)"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white max-w-xs"
                  />
                  <Button
                    onClick={handlePayout}
                    disabled={processingPayout || !payoutAmount}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {processingPayout ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Request Payout
                  </Button>
                </div>
              )}

              <h4 className="text-lg font-semibold text-white mt-8">Payout History</h4>
              {payouts.length === 0 ? (
                <p className="text-gray-400">No payouts yet</p>
              ) : (
                <div className="space-y-2">
                  {payouts.map((p) => (
                    <div key={p.id} className="bg-gray-900/50 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white">${p.amount.toFixed(2)}</p>
                        <p className="text-gray-400 text-xs">{new Date(p.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        p.status === 'completed' ? 'bg-green-900/50 text-green-400' :
                        p.status === 'processing' ? 'bg-yellow-900/50 text-yellow-400' :
                        p.status === 'failed' ? 'bg-red-900/50 text-red-400' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {p.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white">Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">PayPal Email (for payouts)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="email"
                      value={paypalEmail}
                      onChange={(e) => setPaypalEmail(e.target.value)}
                      placeholder="your-paypal@email.com"
                      className="bg-gray-900/50 border-gray-700 text-white"
                    />
                    <Button onClick={handleUpdatePaypal} className="bg-purple-600 hover:bg-purple-700">
                      Save
                    </Button>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-gray-300 mb-2">Account Info</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-400">Agency: <span className="text-white">{agency?.agency_name}</span></p>
                    <p className="text-gray-400">Contact: <span className="text-white">{agency?.contact_name}</span></p>
                    <p className="text-gray-400">Email: <span className="text-white">{agency?.email}</span></p>
                    <p className="text-gray-400">Referral Code: <span className="text-white font-mono">{agency?.referral_code}</span></p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
